import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase";

function fmtTime(v) {
  if (!v) return "—";
  const d = typeof v?.toDate === "function" ? v.toDate() : v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN");
}

function asText(v) {
  if (v == null || v === "") return "—";
  return String(v);
}

function numText(v) {
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : "—";
}

function timeMs(v) {
  if (!v) return 0;
  const d = typeof v?.toDate === "function" ? v.toDate() : v instanceof Date ? v : new Date(v);
  const t = d.getTime();
  return Number.isFinite(t) ? t : 0;
}

export default function AdminLoginsAppTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [appFilter, setAppFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [sortBy, setSortBy] = useState("time");
  const [sortDir, setSortDir] = useState("desc");
  const [selectedRow, setSelectedRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const qy = query(collection(db, "logins"), orderBy("time", "desc"), limit(300));
      const snap = await getDocs(qy);
      const next = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      setRows(next);
    } catch (e) {
      setError(e?.message || "Failed to load logins.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const roleOptions = useMemo(
    () => [...new Set(rows.map((r) => String(r.role || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [rows]
  );
  const appOptions = useMemo(
    () =>
      [...new Set(rows.map((r) => String(r.app?.name || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [rows]
  );
  const platformOptions = useMemo(
    () =>
      [...new Set(rows.map((r) => String(r.device?.platform || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const app = r.app || {};
      const device = r.device || {};
      const location = r.location || {};
      if (roleFilter !== "all" && String(r.role || "").trim() !== roleFilter) return false;
      if (appFilter !== "all" && String(app.name || "").trim() !== appFilter) return false;
      if (platformFilter !== "all" && String(device.platform || "").trim() !== platformFilter) return false;
      if (!q) return true;
      const hay = [
        r.username,
        r.role,
        app.name,
        app.version,
        app.package,
        device.manufacturer,
        device.model,
        device.brand,
        device.platform,
        location.address,
        r.employeeId,
        r.employeesCollection,
      ]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [rows, search, roleFilter, appFilter, platformFilter]);

  const sortedRows = useMemo(() => {
    const out = [...filteredRows];
    const dir = sortDir === "asc" ? 1 : -1;
    out.sort((a, b) => {
      const appA = a.app || {};
      const appB = b.app || {};
      const devA = a.device || {};
      const devB = b.device || {};
      if (sortBy === "time") return (timeMs(a.time || a.timestamp) - timeMs(b.time || b.timestamp)) * dir;
      if (sortBy === "username") return String(a.username || "").localeCompare(String(b.username || "")) * dir;
      if (sortBy === "role") return String(a.role || "").localeCompare(String(b.role || "")) * dir;
      if (sortBy === "app") return String(appA.name || "").localeCompare(String(appB.name || "")) * dir;
      if (sortBy === "version") return String(appA.version || "").localeCompare(String(appB.version || "")) * dir;
      if (sortBy === "device") return String(devA.model || "").localeCompare(String(devB.model || "")) * dir;
      return 0;
    });
    return out;
  }, [filteredRows, sortBy, sortDir]);

  const setSort = (key) => {
    if (sortBy === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(key);
    setSortDir(key === "time" ? "desc" : "asc");
  };

  const summary = useMemo(() => {
    const total = sortedRows.length;
    const uniqueUsers = new Set(sortedRows.map((r) => String(r.username || "").trim()).filter(Boolean)).size;
    const uniqueDevices = new Set(
      sortedRows
        .map((r) => {
          const d = r.device || {};
          return `${d.manufacturer || ""}|${d.model || ""}|${d.androidId || ""}`;
        })
        .filter((x) => x !== "||")
    ).size;
    return { total, uniqueUsers, uniqueDevices };
  }, [sortedRows]);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Logins (App)</h2>
          <p className="text-sm text-slate-600">
            Firestore collection <code className="rounded bg-slate-100 px-1">logins</code> with app/device/location snapshots.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">Rows</p>
          <p className="text-lg font-semibold text-slate-900">{summary.total}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">Unique users</p>
          <p className="text-lg font-semibold text-slate-900">{summary.uniqueUsers}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">Unique devices</p>
          <p className="text-lg font-semibold text-slate-900">{summary.uniqueDevices}</p>
        </div>
      </div>

      <div className="mb-4 grid gap-2 md:grid-cols-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search username, app, device, address…"
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        >
          <option value="all">All roles</option>
          {roleOptions.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
        <select
          value={appFilter}
          onChange={(e) => setAppFilter(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        >
          <option value="all">All apps</option>
          {appOptions.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
        <select
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        >
          <option value="all">All platforms</option>
          {platformOptions.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
      </div>

      {error ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="overflow-auto rounded-xl border border-slate-200">
        <table className="min-w-[1300px] w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="p-2 text-left">
                <button type="button" onClick={() => setSort("time")} className="font-semibold hover:underline">
                  Time {sortBy === "time" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                </button>
              </th>
              <th className="p-2 text-left">
                <button type="button" onClick={() => setSort("username")} className="font-semibold hover:underline">
                  Username {sortBy === "username" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                </button>
              </th>
              <th className="p-2 text-left">
                <button type="button" onClick={() => setSort("role")} className="font-semibold hover:underline">
                  Role {sortBy === "role" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                </button>
              </th>
              <th className="p-2 text-left">
                <button type="button" onClick={() => setSort("app")} className="font-semibold hover:underline">
                  App {sortBy === "app" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                </button>
              </th>
              <th className="p-2 text-left">
                <button type="button" onClick={() => setSort("device")} className="font-semibold hover:underline">
                  Device {sortBy === "device" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                </button>
              </th>
              <th className="p-2 text-left">Location</th>
              <th className="p-2 text-left">Employee</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => {
              const app = r.app || {};
              const device = r.device || {};
              const location = r.location || {};
              return (
                <tr
                  key={r.id}
                  className="border-t border-slate-100 align-top cursor-pointer hover:bg-indigo-50/40"
                  onClick={() => setSelectedRow(r)}
                  title="Click to view full details"
                >
                  <td className="p-2 whitespace-nowrap">{fmtTime(r.time || r.timestamp)}</td>
                  <td className="p-2">{asText(r.username)}</td>
                  <td className="p-2">{asText(r.role)}</td>
                  <td className="p-2">
                    <div>{asText(app.name)}</div>
                    <div className="text-xs text-slate-600">Package: {asText(app.package)}</div>
                    <div className="text-xs text-slate-600">
                      Version: {asText(app.version)} · Build: {asText(app.buildNumber)}
                    </div>
                  </td>
                  <td className="p-2">
                    <div>
                      {asText(device.manufacturer)} {asText(device.model)}
                    </div>
                    <div className="text-xs text-slate-600">
                      Brand: {asText(device.brand)} · Device: {asText(device.device)}
                    </div>
                    <div className="text-xs text-slate-600">
                      Android: {asText(device.release)} (SDK {numText(device.sdkInt)}) · ID {asText(device.androidId)}
                    </div>
                  </td>
                  <td className="p-2">
                    <div className="text-xs">
                      {numText(location.lat)}, {numText(location.lng)}
                    </div>
                    <div className="text-xs text-slate-600">Accuracy: {numText(location.accuracy)}</div>
                    <div className="text-xs text-slate-600 max-w-[26rem]">{asText(location.address)}</div>
                  </td>
                  <td className="p-2 text-xs">
                    <div>Employee ID: {asText(r.employeeId)}</div>
                    <div>Collection: {asText(r.employeesCollection)}</div>
                  </td>
                </tr>
              );
            })}
            {!loading && sortedRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-4 text-center text-sm text-slate-500">
                  No login rows found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {selectedRow ? (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-4xl rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Login full details</h3>
                <p className="text-xs text-slate-500">
                  {asText(selectedRow.username)} · {fmtTime(selectedRow.time || selectedRow.timestamp)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedRow(null)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            <div className="max-h-[75vh] overflow-auto p-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Basic</p>
                  <p>Username: {asText(selectedRow.username)}</p>
                  <p>Role: {asText(selectedRow.role)}</p>
                  <p>User ID: {asText(selectedRow.userId)}</p>
                  <p>Employee ID: {asText(selectedRow.employeeId)}</p>
                  <p>Employees collection: {asText(selectedRow.employeesCollection)}</p>
                  <p>Time: {fmtTime(selectedRow.time)}</p>
                  <p>Timestamp: {fmtTime(selectedRow.timestamp)}</p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                  <p className="text-xs uppercase tracking-wide text-slate-500">App</p>
                  <p>Name: {asText(selectedRow.app?.name)}</p>
                  <p>Package: {asText(selectedRow.app?.package)}</p>
                  <p>Version: {asText(selectedRow.app?.version)}</p>
                  <p>Build number: {asText(selectedRow.app?.buildNumber)}</p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm md:col-span-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Device</p>
                  <div className="grid gap-2 sm:grid-cols-3">
                    <p>Manufacturer: {asText(selectedRow.device?.manufacturer)}</p>
                    <p>Model: {asText(selectedRow.device?.model)}</p>
                    <p>Brand: {asText(selectedRow.device?.brand)}</p>
                    <p>Device: {asText(selectedRow.device?.device)}</p>
                    <p>Product: {asText(selectedRow.device?.product)}</p>
                    <p>Hardware: {asText(selectedRow.device?.hardware)}</p>
                    <p>Platform: {asText(selectedRow.device?.platform)}</p>
                    <p>Release: {asText(selectedRow.device?.release)}</p>
                    <p>SDK Int: {numText(selectedRow.device?.sdkInt)}</p>
                    <p className="sm:col-span-3">Android ID: {asText(selectedRow.device?.androidId)}</p>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm md:col-span-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Location</p>
                  <p>
                    Lat/Lng: {numText(selectedRow.location?.lat)}, {numText(selectedRow.location?.lng)}
                  </p>
                  <p>Accuracy: {numText(selectedRow.location?.accuracy)}</p>
                  <p>Address: {asText(selectedRow.location?.address)}</p>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-950 p-3">
                <p className="mb-2 text-xs uppercase tracking-wide text-slate-300">Raw JSON</p>
                <pre className="overflow-auto text-xs text-slate-100">
                  {JSON.stringify(selectedRow, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

